import { FC, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable } from "@fortawesome/free-solid-svg-icons";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import capitalizeFirst from "src/shared/helpers/upper-case";
import faArrowDown from "src/assets/icon/sort-down.svg";
import faArrowUp from "src/assets/icon/sort-up.svg";
import "./asset-preview-table.scss";

export interface AssetPreviewColumn {
  /** key into each row object */
  label: string;
  /** column header text */
  title: string;
  /** optional fixed width, e.g. "160px" */
  width?: string;
  /** set true to hide the sort button on this column */
  excludeSort?: boolean;
  /** mark column as currently sorted */
  isSorted?: boolean;
}

export interface AssetPreviewTableProps {
  columns: AssetPreviewColumn[];
  /** current page of rows, as returned by the API */
  data: any[];
  /** total row count across all pages (API: numberOfRecords) */
  totalNumberOfRecords: number;
  /** current page size */
  pageSize: number;
  /** current page number, 1-based */
  current: number;
  onChangePage: (page: number) => void;
  /** optional page size change handler; omit to hide the rows-per-page control */
  onChangePageSize?: (pageSize: number) => void;
  pageSizeOptions?: number[];

  /** name shown in the header bar, e.g. the uploaded file name */
  fileName?: string;
  /** how many rows were flagged as changed, across all pages (API: changedCount) */
  changedCount?: number;

  /** field on each row that marks it as changed */
  changedField?: string;

  /** controlled "changed only" filter; parent should re-fetch filtered data when this changes */
  changedOnly?: boolean;
  onChangedOnlyToggle?: (changedOnly: boolean) => void;

  isLoadingData?: boolean;
  wrapperHeight?: string;
  /** called when the user clicks a column header to sort */
  onChangeSort?: (sortBy: string, sortDirection: "asc" | "desc") => void;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const AssetPreviewTableComponent: FC<AssetPreviewTableProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangePageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  fileName,
  changedCount,
  changedField = "isChanged",
  changedOnly = false,
  onChangedOnlyToggle,
  isLoadingData = false,
  wrapperHeight = "700px",
  onChangeSort,
}) => {
  // ── sorting state (mirrors table.component.tsx) ──
  const [hoverList, setHoverList] = useState<any[]>([]);
  const [isDesc, setIsDesc] = useState(true);
  const [sortBy, setSortBy] = useState<string | null>(null);

  const handleMouseEnter = (col: AssetPreviewColumn) => {
    setHoverList((prev) => (prev.includes(col) ? prev : [...prev, col]));
  };

  const handleMouseLeave = (col: AssetPreviewColumn) => {
    setHoverList((prev) => prev.filter((x) => x !== col));
  };

  const handleSort = (label: string) => {
    setSortBy(label);
    setIsDesc((prev) => !prev);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!onChangeSort || !sortBy) return;
    const sortOrder = isDesc ? "desc" : "asc";
    onChangeSort(capitalizeFirst(sortBy), sortOrder);
  }, [isDesc, onChangeSort, sortBy]);

  const showChangedOnly = changedOnly;

  const totalPages = Math.max(
    1,
    Math.ceil((totalNumberOfRecords || 0) / (pageSize || 1)),
  );

  // All filtering is done server-side; display the data as-is.
  const visibleRows = data;

  const handleToggleChangedOnly = () => {
    onChangedOnlyToggle?.(!showChangedOnly);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChangePageSize?.(Number(e.target.value));
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages || page === current) return;
    onChangePage(page);
  };

  return (
    <SkeletonComponent
      wrapperHeight={wrapperHeight}
      skeletonHeight="50px"
      isLoading={isLoadingData}
      count={8}
      isShowCardStyle={true}
    >
      <div className="asset-preview-table">
        {/* ── summary bar ── */}
        <div className="asset-preview-table__summary">
          <div className="asset-preview-table__summary-left">
            <FontAwesomeIcon
              icon={faTable}
              className="asset-preview-table__file-icon"
            />
            {fileName && (
              <span className="asset-preview-table__file-name">{fileName}</span>
            )}
            <span className="asset-preview-table__file-meta">
              {totalNumberOfRecords} row{totalNumberOfRecords === 1 ? "" : "s"}{" "}
              found
              {" — review before processing"}
            </span>
          </div>

          <div className="asset-preview-table__summary-right">
            <button
              type="button"
              className={`asset-preview-table__chip ${showChangedOnly ? "asset-preview-table__chip--active" : ""}`}
              onClick={handleToggleChangedOnly}
              aria-pressed={showChangedOnly}
            >
              <span className="asset-preview-table__chip-dot" />
              Changed only
              {typeof changedCount === "number" ? ` (${changedCount})` : ""}
            </button>
            <span className="asset-preview-table__file-meta">
              {visibleRows?.length ?? 0} of {data?.length ?? 0}
            </span>
          </div>
        </div>

        {/* ── table ── */}
        {visibleRows?.length ? (
          <div className="asset-preview-table__scroll">
            <table className="asset-preview-table__table">
              <thead>
                <tr>
                  {columns.map((col) => {
                    let sortIcon: React.ReactNode = null;
                    if (!col.excludeSort) {
                      sortIcon = isDesc ? (
                        <img src={faArrowDown} alt="sort descending" />
                      ) : (
                        <img src={faArrowUp} alt="sort ascending" />
                      );
                    }

                    const isActive =
                      (col.hasOwnProperty("isSorted") && col.isSorted) ||
                      hoverList.includes(col);

                    return (
                      <th
                        key={col.label}
                        style={{ width: col.width }}
                        onMouseEnter={() => handleMouseEnter(col)}
                        onMouseLeave={() => handleMouseLeave(col)}
                      >
                        <div className="d-flex justify-center items-center">
                          {col.title}
                          {!col.excludeSort && (
                            <button
                              type="button"
                              aria-label={`Sort by ${col.title || col.label}`}
                              onClick={() => handleSort(col.label)}
                              className={`flex ms-2 ${isActive ? "activeSort" : "disabled"} table-sort-button`}
                              onMouseEnter={() => handleMouseEnter(col)}
                              onMouseLeave={() => handleMouseLeave(col)}
                              onFocus={() => handleMouseEnter(col)}
                              onBlur={() => handleMouseLeave(col)}
                              style={{ cursor: "pointer" }}
                            >
                              {sortIcon}
                            </button>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row: any, i: number) => {
                  const isChanged = !!row?.[changedField];
                  return (
                    <tr
                      key={row?.assetCode ?? row?.id ?? i}
                      className={isChanged ? "is-changed" : ""}
                    >
                      {columns.map((col) => {
                        const value = row?.[col.label];
                        const display =
                          value === null || value === undefined || value === ""
                            ? "-"
                            : String(value);
                        return (
                          <td key={col.label} style={{ width: col.width }}>
                            <span className="asset-preview-table__cell-text">
                              {display}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="asset-preview-table__empty">
            <div className="asset-preview-table__empty-title">
              No rows to show
            </div>
            <div className="asset-preview-table__empty-desc">
              {showChangedOnly
                ? "No changed rows on this page."
                : "Add some new data please."}
            </div>
          </div>
        )}

        {/* ── footer / pagination ── */}
        <div className="asset-preview-table__footer">
          {onChangePageSize && (
            <div className="asset-preview-table__page-size">
              <label htmlFor="asset-preview-page-size">Rows per page</label>
              <select
                id="asset-preview-page-size"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="asset-preview-table__pager">
            <button
              type="button"
              aria-label="First page"
              disabled={current <= 1}
              onClick={() => goToPage(1)}
            >
              «
            </button>
            <button
              type="button"
              aria-label="Previous page"
              disabled={current <= 1}
              onClick={() => goToPage(current - 1)}
            >
              ‹
            </button>
            <span className="asset-preview-table__pager-label">
              Page {current} of {totalPages}
            </span>
            <button
              type="button"
              aria-label="Next page"
              disabled={current >= totalPages}
              onClick={() => goToPage(current + 1)}
            >
              ›
            </button>
            <button
              type="button"
              aria-label="Last page"
              disabled={current >= totalPages}
              onClick={() => goToPage(totalPages)}
            >
              »
            </button>
          </div>
        </div>
        {/* ── legend ── */}
        <div className="asset-preview-table__legend">
          <span className="asset-preview-table__legend-swatch is-changed" />
          <span>Highlighted rows were changed</span>
        </div>
      </div>
    </SkeletonComponent>
  );
};

export default AssetPreviewTableComponent;
